import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ContentLayout, StepperCard } from '@ibk/shared-layout';
import { useTranslation } from '@ibk/shared-translation';
import { ReleaseType } from '@ibk/shared-types';
import { Alert, Icon, Loading } from '@ibk/shared-ui';
import { formatCurrency } from '@ibk/shared-utils';
import { BeneficiaryValuesForm } from '../../../../components/BeneficiaryValuesForm';
import { useActiveContractProducts, useBenefitContract, useVoucherBeneficiaries } from '../../../../hooks';
import { useBusinessDays } from '../../../../hooks/useBusinessDays';
import { BeneficiaryStatusInProduct } from '../../../../types';
import { convertFilterDataToSearchRequest, getUnformatedValue, hasAnyFilter, isActiveStatus, } from '../../../../utils/beneficiaryUtils';
import { hasSelectedBeneficiaryWithoutCard } from '../../../../utils/cardStatusUtils';
import { NewRechargeStep, NewRechargeStepStatus, SimulationState, } from '../../types';
import { NewRechargeSidebar } from '../NewRechargeSidebar';
import { SpreadsheetImportModal } from '../SpreadsheetImportModal/SpreadsheetImportModal';
import { normalizeCpf } from '../SpreadsheetImportModal/utils/outcomeMapper';
import styles from './BeneficiariesValues.module.css';
import { applyGlobalValuesToBeneficiaries, getInitialBeneficiaryHistory, getInitialReleaseTypeFromProduct, } from './utils/beneficiariesValuesUtils';
const DEFAULT_CURRENCY_VALUE = 'R$ 0,00';
export function BeneficiariesValues({ simulationState, formData, onUpdateBeneficiariesData, onComplete, onReturn, onStepChange, isBeneficiaryDiscountEnabled, headerStepper, }) {
    const { t } = useTranslation();
    const [showAlert, setShowAlert] = useState(false);
    const [showInfoAlert, setShowInfoAlert] = useState(true);
    const [errorMessage, setErrorMessage] = useState('');
    const [showZeroValueError, setShowZeroValueError] = useState(false);
    const { contractData } = useBenefitContract();
    const { products, isLoading: isLoadingProducts } = useActiveContractProducts(contractData ? { voucherContractId: contractData.id } : { voucherContractId: '' });
    const [beneficiaries, setBeneficiaries] = useState(formData.beneficiaries ?? []);
    const [releaseType, setReleaseType] = useState(() => {
        if (products.length > 0 && formData.voucherTypeId) {
            return getInitialReleaseTypeFromProduct(products, formData.voucherTypeId, formData.releaseType);
        }
        return formData.releaseType ?? ReleaseType.MONTHLY;
    });
    const [beneficiaryHistory, setBeneficiaryHistory] = useState(() => {
        if (products.length > 0 && formData.voucherTypeId) {
            return getInitialBeneficiaryHistory(products, formData.voucherTypeId, formData);
        }
        return {
            monthly: {
                values: formData.releaseType === ReleaseType.MONTHLY ? (formData.beneficiaries ?? null) : null,
                globalValue: formData.releaseType === ReleaseType.MONTHLY
                    ? (formData.globalValue ?? DEFAULT_CURRENCY_VALUE)
                    : DEFAULT_CURRENCY_VALUE,
            },
            daily: {
                values: formData.releaseType === ReleaseType.DAILY ? (formData.beneficiaries ?? null) : null,
                globalValue: formData.releaseType === ReleaseType.DAILY
                    ? (formData.globalValue ?? DEFAULT_CURRENCY_VALUE)
                    : DEFAULT_CURRENCY_VALUE,
            },
        };
    });
    const [businessDays, setBusinessDays] = useState(formData.businessDays ?? 0);
    const selectedBeneficiariesListRef = useRef(formData.selectedBeneficiariesList);
    const [appliedFilters, setAppliedFilters] = useState(formData.beneficiaryFilters ?? null);
    const previousFiltersRef = useRef('');
    const [isFiltering, setIsFiltering] = useState(false);
    const hasBeenInitializedRef = useRef(false);
    const [showImportModal, setShowImportModal] = useState(false);
    const globalValue = beneficiaryHistory[releaseType === ReleaseType.MONTHLY ? 'monthly' : 'daily'].globalValue;
    const buildSearchFilters = useCallback((filters) => {
        if (!filters) {
            return undefined;
        }
        return convertFilterDataToSearchRequest(filters, selectedBeneficiariesListRef.current);
    }, []);
    const searchFilters = useMemo(() => (appliedFilters ? buildSearchFilters(appliedFilters) : undefined), [appliedFilters, buildSearchFilters]);
    const searchFiltersKey = useMemo(() => JSON.stringify(searchFilters), [searchFilters]);
    const { voucherBeneficiaries, isLoading: isLoadingBeneficiaries, searchSync, } = useVoucherBeneficiaries({
        voucherContractId: contractData?.id ?? '',
        voucherTypeId: formData.voucherTypeId ?? '',
        searchFilters: undefined,
    });
    const competenceString = formData.competenceDate
        ? `${formData.competenceDate.getFullYear()}-${String(formData.competenceDate.getMonth() + 1).padStart(2, '0')}`
        : undefined;
    const { businessDays: businessDaysData, isLoading: isLoadingBusinessDays } = useBusinessDays({
        competence: competenceString,
        enabled: false,
    });
    const initializeBeneficiariesWithFilter = useCallback((voucherBeneficiaries, appliedFilters) => {
        if (appliedFilters) {
            return;
        }
        if (hasBeenInitializedRef.current) {
            return;
        }
        if (selectedBeneficiariesListRef.current?.length) {
            const selectedMap = new Map(selectedBeneficiariesListRef.current.map(selected => [selected.beneficiaryId, selected]));
            const merged = voucherBeneficiaries.map(beneficiary => {
                const sel = selectedMap.get(beneficiary.id);
                return {
                    ...beneficiary,
                    productValue: sel?.value ?? beneficiary.productValue ?? 0,
                    productDaysQuantity: sel?.daysQuantity ?? beneficiary.productDaysQuantity ?? 0,
                    productDiscountValue: sel?.discountValue ?? beneficiary.productDiscountValue ?? 0,
                    statusInProduct: sel ? BeneficiaryStatusInProduct.ACTIVE : BeneficiaryStatusInProduct.INACTIVE,
                };
            });
            setBeneficiaries(merged);
            hasBeenInitializedRef.current = true;
            const selectedBeneficiaries = merged
                .filter(beneficiary => isActiveStatus(beneficiary.statusInProduct))
                .map(beneficiary => ({
                beneficiaryId: beneficiary.id,
                value: beneficiary.productValue,
                daysQuantity: beneficiary.productDaysQuantity ?? 0,
                discountValue: beneficiary.productDiscountValue ?? 0,
            }));
            selectedBeneficiariesListRef.current = selectedBeneficiaries;
            onUpdateBeneficiariesData({
                beneficiaries: merged,
                selectedBeneficiariesList: selectedBeneficiaries,
                selectedBeneficiaries: selectedBeneficiaries.length,
            });
            return;
        }
        const initialBeneficiaries = voucherBeneficiaries.map(beneficiary => ({
            ...beneficiary,
            productValue: beneficiary.productValue ?? 0,
            productDaysQuantity: beneficiary.productDaysQuantity ?? 0,
        }));
        setBeneficiaries(initialBeneficiaries);
        hasBeenInitializedRef.current = true;
        const selectedBeneficiaries = initialBeneficiaries
            .filter(beneficiary => isActiveStatus(beneficiary.statusInProduct))
            .map(beneficiary => ({
            beneficiaryId: beneficiary.id,
            value: beneficiary.productValue,
            daysQuantity: beneficiary.productDaysQuantity ?? 0,
        }));
        selectedBeneficiariesListRef.current = selectedBeneficiaries;
        onUpdateBeneficiariesData({
            selectedBeneficiariesList: selectedBeneficiaries,
        });
    }, [onUpdateBeneficiariesData]);
    const beneficiaryInitializer = useMemo(() => ({
        initialize: (voucherBeneficiaries, appliedFilters) => {
            initializeBeneficiariesWithFilter(voucherBeneficiaries, appliedFilters);
        },
    }), [initializeBeneficiariesWithFilter]);
    useEffect(() => {
        if (!voucherBeneficiaries?.length || appliedFilters) {
            return;
        }
        const selectedList = selectedBeneficiariesListRef.current ?? formData.selectedBeneficiariesList;
        if (beneficiaries.length > 0 && voucherBeneficiaries.length === beneficiaries.length) {
            hasBeenInitializedRef.current = true;
            return;
        }
        const needsPrefillMerge = Boolean(selectedList?.length) &&
            !hasBeenInitializedRef.current &&
            (beneficiaries.length === 0 || voucherBeneficiaries.length > beneficiaries.length);
        if (needsPrefillMerge) {
            beneficiaryInitializer.initialize(voucherBeneficiaries, appliedFilters);
            return;
        }
        if (!hasBeenInitializedRef.current && beneficiaries.length === 0) {
            beneficiaryInitializer.initialize(voucherBeneficiaries, appliedFilters);
        }
    }, [
        voucherBeneficiaries,
        beneficiaries.length,
        appliedFilters,
        beneficiaryInitializer,
        formData.selectedBeneficiariesList,
    ]);
    useEffect(() => {
        if (formData.businessDays !== undefined && formData.businessDays !== null) {
            setBusinessDays(formData.businessDays);
        }
    }, [formData.businessDays]);
    useEffect(() => {
        if (!isLoadingProducts && products.length > 0 && formData.voucherTypeId) {
            const productReleaseType = getInitialReleaseTypeFromProduct(products, formData.voucherTypeId, formData.releaseType);
            if (productReleaseType !== releaseType) {
                setReleaseType(productReleaseType);
            }
            const initialHistory = getInitialBeneficiaryHistory(products, formData.voucherTypeId, formData);
            setBeneficiaryHistory(initialHistory);
        }
    }, [isLoadingProducts, products, formData.voucherTypeId]);
    useEffect(() => {
        if (formData.selectedBeneficiariesList) {
            selectedBeneficiariesListRef.current = formData.selectedBeneficiariesList;
        }
    }, [formData.selectedBeneficiariesList]);
    useEffect(() => {
        if (formData.beneficiaryFilters !== undefined) {
            const filtersString = JSON.stringify(formData.beneficiaryFilters);
            const appliedFiltersString = JSON.stringify(appliedFilters);
            if (filtersString !== appliedFiltersString) {
                setAppliedFilters(formData.beneficiaryFilters);
                if (formData.beneficiaryFilters) {
                    const filtersKey = JSON.stringify(formData.beneficiaryFilters ? buildSearchFilters(formData.beneficiaryFilters) : undefined);
                    previousFiltersRef.current = filtersKey;
                }
                else {
                    previousFiltersRef.current = '';
                }
            }
        }
    }, [formData.beneficiaryFilters, appliedFilters, buildSearchFilters]);
    useEffect(() => {
        if (!isLoadingProducts &&
            !isLoadingBeneficiaries &&
            !isLoadingBusinessDays &&
            beneficiaries.length > 0 &&
            globalValue &&
            globalValue !== DEFAULT_CURRENCY_VALUE &&
            products.length > 0 &&
            formData.voucherTypeId) {
            const product = products.find(p => p.voucherType === formData.voucherTypeId);
            let currentBusinessDays = 0;
            if (releaseType === ReleaseType.DAILY) {
                currentBusinessDays = formData.businessDays ?? businessDays;
            }
            if (product?.releaseType === ReleaseType.DAILY && currentBusinessDays > 0) {
                const needsApplication = beneficiaries.some(beneficiary => isActiveStatus(beneficiary.statusInProduct) &&
                    (beneficiary.productValue === 0 || beneficiary.productDaysQuantity === 0));
                if (needsApplication) {
                    const updatedBeneficiaries = applyGlobalValuesToBeneficiaries(beneficiaries, globalValue, releaseType, currentBusinessDays);
                    setBeneficiaries(updatedBeneficiaries);
                }
            }
        }
    }, [
        isLoadingProducts,
        isLoadingBeneficiaries,
        isLoadingBusinessDays,
        beneficiaries,
        globalValue,
        releaseType,
        businessDaysData,
        businessDays,
        products,
        formData.voucherTypeId,
        formData.businessDays,
    ]);
    const breadcrumbs = [
        { label: t('recharge.breadcrumb.benefits'), href: '/beneficios' },
        { label: t('recharge.breadcrumb.new-recharge'), isActive: true },
    ];
    const currentStep = {
        id: NewRechargeStep.BENEFICIARIES_VALUES,
        title: t('recharge.step.beneficiaries-values'),
        number: 3,
        status: NewRechargeStepStatus.ACTIVE,
    };
    const handleBack = useCallback(() => {
        onReturn();
    }, [onReturn]);
    const updateSelectedBeneficiariesList = useCallback((beneficiaries) => {
        const currentSelectedMap = new Map(selectedBeneficiariesListRef.current?.map(selected => [selected.beneficiaryId, selected]) ?? []);
        const visibleBeneficiariesIds = new Set(beneficiaries.map(b => b.id));
        beneficiaries.forEach(beneficiary => {
            if (isActiveStatus(beneficiary.statusInProduct)) {
                currentSelectedMap.set(beneficiary.id, {
                    beneficiaryId: beneficiary.id,
                    value: beneficiary.productValue,
                    daysQuantity: beneficiary.productDaysQuantity ?? 0,
                    discountValue: beneficiary.productDiscountValue ?? 0,
                });
            }
            else if (visibleBeneficiariesIds.has(beneficiary.id) && currentSelectedMap.has(beneficiary.id)) {
                currentSelectedMap.delete(beneficiary.id);
            }
        });
        const newSelectedBeneficiariesList = Array.from(currentSelectedMap.values());
        const currentListString = JSON.stringify(selectedBeneficiariesListRef.current ?? []);
        const newListString = JSON.stringify(newSelectedBeneficiariesList);
        const changed = currentListString !== newListString;
        if (changed) {
            selectedBeneficiariesListRef.current = newSelectedBeneficiariesList;
        }
        return { list: newSelectedBeneficiariesList, changed };
    }, []);
    const buildUpdateDataWithFilter = useCallback((beneficiaries, globalValue, businessDays, releaseType) => {
        const { list: selectedList, changed: selectedListChanged } = updateSelectedBeneficiariesList(beneficiaries);
        const updateData = {
            beneficiaries,
            globalValue,
            businessDays,
            releaseType,
            selectedBeneficiaries: selectedList.length,
            amountDaily: releaseType === ReleaseType.DAILY ? getUnformatedValue(globalValue) : undefined,
        };
        if (selectedListChanged) {
            updateData.selectedBeneficiariesList = selectedList;
        }
        return updateData;
    }, [updateSelectedBeneficiariesList]);
    const updateDataBuilder = useMemo(() => ({
        build: (beneficiaries, globalValue, businessDays, releaseType) => {
            return buildUpdateDataWithFilter(beneficiaries, globalValue, businessDays, releaseType);
        },
    }), [buildUpdateDataWithFilter]);
    useEffect(() => {
        const updateData = updateDataBuilder.build(beneficiaries, globalValue, businessDays, releaseType);
        onUpdateBeneficiariesData(updateData);
    }, [beneficiaries, globalValue, businessDays, releaseType, updateDataBuilder, onUpdateBeneficiariesData]);
    const effectiveSelectedBeneficiariesList = selectedBeneficiariesListRef.current ?? formData.selectedBeneficiariesList ?? [];
    const hasMonthlyZeroValue = releaseType === ReleaseType.MONTHLY &&
        effectiveSelectedBeneficiariesList.length > 0 &&
        effectiveSelectedBeneficiariesList.some(s => !s.value || s.value === 0);
    const hasDailyZeroValue = releaseType === ReleaseType.DAILY &&
        effectiveSelectedBeneficiariesList.length > 0 &&
        effectiveSelectedBeneficiariesList.some(s => !s.daysQuantity || s.daysQuantity === 0);
    useEffect(() => {
        if (!hasMonthlyZeroValue && !hasDailyZeroValue) {
            setShowZeroValueError(false);
        }
    }, [hasMonthlyZeroValue, hasDailyZeroValue]);
    useEffect(() => {
        if (!showZeroValueError)
            return;
        const timer = setTimeout(() => setShowZeroValueError(false), 5000);
        return () => clearTimeout(timer);
    }, [showZeroValueError]);
    const handleNext = useCallback(async () => {
        if (hasMonthlyZeroValue || hasDailyZeroValue) {
            setShowZeroValueError(true);
            return;
        }
        try {
            const requiresCardDelivery = hasSelectedBeneficiaryWithoutCard(selectedBeneficiariesListRef.current ?? formData.selectedBeneficiariesList ?? [], voucherBeneficiaries);
            onComplete(true, undefined, undefined, requiresCardDelivery);
        }
        catch (error) {
            setErrorMessage(t('recharge.step.beneficiaries-values.error.message'));
            setShowAlert(true);
        }
    }, [onComplete, t, hasMonthlyZeroValue, hasDailyZeroValue, formData.selectedBeneficiariesList, voucherBeneficiaries]);
    const handleGlobalValueChange = useCallback((value) => {
        const type = releaseType === ReleaseType.MONTHLY ? 'monthly' : 'daily';
        setBeneficiaryHistory(prev => ({
            ...prev,
            [type]: {
                ...prev[type],
                globalValue: value,
            },
        }));
    }, [releaseType]);
    const applySelectedBeneficiariesToVoucherBeneficiaries = useCallback((voucherBeneficiaries, selectedBeneficiaries, useSavedValues = false) => {
        const selectedMap = new Map(selectedBeneficiaries.map(selected => [selected.beneficiaryId, selected]));
        const currentSelectedMap = useSavedValues
            ? new Map(selectedBeneficiariesListRef.current?.map(selected => [selected.beneficiaryId, selected]) ?? [])
            : null;
        return voucherBeneficiaries.map(beneficiary => {
            const isSelected = selectedMap.has(beneficiary.id);
            const savedData = currentSelectedMap?.get(beneficiary.id);
            return {
                ...beneficiary,
                statusInProduct: isSelected ? BeneficiaryStatusInProduct.ACTIVE : BeneficiaryStatusInProduct.INACTIVE,
                productValue: savedData?.value ?? beneficiary.productValue ?? 0,
                productDaysQuantity: savedData?.daysQuantity ?? beneficiary.productDaysQuantity ?? 0,
            };
        });
    }, []);
    const updateBeneficiariesWithFilterEnabled = useCallback((newBeneficiaries, returnedSelected, useSelectedForStatus = false) => {
        let updatedBeneficiaries;
        let updatedSelectedBeneficiaries;
        const currentSelectedMap = new Map(selectedBeneficiariesListRef.current?.map(selected => [selected.beneficiaryId, selected]) ?? []);
        if (useSelectedForStatus && returnedSelected !== undefined) {
            const returnedSelectedMap = new Map(returnedSelected.map(selected => [selected.beneficiaryId, selected]));
            const mergedSelected = returnedSelected.map(selected => {
                const existing = currentSelectedMap.get(selected.beneficiaryId);
                if (existing) {
                    return {
                        beneficiaryId: selected.beneficiaryId,
                        value: existing.value,
                        daysQuantity: existing.daysQuantity,
                    };
                }
                return selected;
            });
            currentSelectedMap.forEach((selected, beneficiaryId) => {
                if (!returnedSelectedMap.has(beneficiaryId)) {
                    mergedSelected.push(selected);
                }
            });
            updatedBeneficiaries = applySelectedBeneficiariesToVoucherBeneficiaries(newBeneficiaries, mergedSelected, true);
            updatedSelectedBeneficiaries = mergedSelected;
        }
        else {
            updatedBeneficiaries = newBeneficiaries;
            const visibleBeneficiariesIds = new Set(updatedBeneficiaries.map(b => b.id));
            updatedBeneficiaries.forEach(beneficiary => {
                if (isActiveStatus(beneficiary.statusInProduct)) {
                    const existing = currentSelectedMap.get(beneficiary.id);
                    currentSelectedMap.set(beneficiary.id, {
                        beneficiaryId: beneficiary.id,
                        value: existing?.value ?? beneficiary.productValue,
                        daysQuantity: existing?.daysQuantity ?? beneficiary.productDaysQuantity ?? 0,
                    });
                }
                else if (visibleBeneficiariesIds.has(beneficiary.id) && currentSelectedMap.has(beneficiary.id)) {
                    currentSelectedMap.delete(beneficiary.id);
                }
            });
            updatedSelectedBeneficiaries = Array.from(currentSelectedMap.values());
        }
        setBeneficiaries(updatedBeneficiaries);
        const previousListString = JSON.stringify(selectedBeneficiariesListRef.current ?? []);
        const newListString = JSON.stringify(updatedSelectedBeneficiaries);
        selectedBeneficiariesListRef.current = updatedSelectedBeneficiaries;
        if (previousListString !== newListString) {
            onUpdateBeneficiariesData({
                selectedBeneficiariesList: updatedSelectedBeneficiaries,
            });
        }
    }, [applySelectedBeneficiariesToVoucherBeneficiaries, onUpdateBeneficiariesData]);
    const handleUpdateBeneficiariesWithFilter = useCallback((updatedBeneficiaries) => {
        updateBeneficiariesWithFilterEnabled(updatedBeneficiaries);
    }, [updateBeneficiariesWithFilterEnabled]);
    const beneficiaryUpdater = useMemo(() => handleUpdateBeneficiariesWithFilter, [handleUpdateBeneficiariesWithFilter]);
    const performSearchWithFilters = useCallback(async () => {
        if (!searchFilters || !searchSync) {
            return;
        }
        setIsFiltering(true);
        previousFiltersRef.current = searchFiltersKey;
        try {
            const result = await searchSync(searchFilters);
            updateBeneficiariesWithFilterEnabled(result.beneficiaries, result.selected || [], true);
        }
        catch (error) {
            console.error('Error searching beneficiaries:', error);
        }
        finally {
            setIsFiltering(false);
        }
    }, [searchFilters, searchSync, searchFiltersKey, updateBeneficiariesWithFilterEnabled]);
    const getSelectedBeneficiariesToUse = useCallback((voucherBeneficiaries, currentSelected) => {
        if (currentSelected !== undefined) {
            return currentSelected;
        }
        return voucherBeneficiaries
            .filter(beneficiary => isActiveStatus(beneficiary.statusInProduct))
            .map(beneficiary => ({
            beneficiaryId: beneficiary.id,
            value: beneficiary.productValue,
            daysQuantity: beneficiary.productDaysQuantity ?? 0,
        }));
    }, []);
    const processVoucherBeneficiariesWithFilter = useCallback((voucherBeneficiaries, useRefForSelected = false) => {
        const currentSelected = useRefForSelected
            ? selectedBeneficiariesListRef.current
            : formData.selectedBeneficiariesList;
        const selectedBeneficiariesToUse = getSelectedBeneficiariesToUse(voucherBeneficiaries, currentSelected);
        const savedSelectedMap = new Map(selectedBeneficiariesListRef.current?.map(selected => [selected.beneficiaryId, selected]) ?? []);
        const selectedMap = new Map(selectedBeneficiariesToUse.map(selected => [selected.beneficiaryId, selected]));
        const beneficiariesWithPreservedValues = voucherBeneficiaries.map(beneficiary => {
            const isSelected = selectedMap.has(beneficiary.id);
            const savedData = savedSelectedMap.get(beneficiary.id);
            return {
                ...beneficiary,
                statusInProduct: isSelected ? BeneficiaryStatusInProduct.ACTIVE : BeneficiaryStatusInProduct.INACTIVE,
                productValue: savedData?.value ?? beneficiary.productValue ?? 0,
                productDaysQuantity: savedData?.daysQuantity ?? beneficiary.productDaysQuantity ?? 0,
                productDiscountValue: savedData?.discountValue ?? beneficiary.productDiscountValue ?? 0,
            };
        });
        updateBeneficiariesWithFilterEnabled(beneficiariesWithPreservedValues, selectedBeneficiariesToUse);
    }, [getSelectedBeneficiariesToUse, updateBeneficiariesWithFilterEnabled, formData.selectedBeneficiariesList]);
    const clearFiltersWithSearchSync = useCallback(async () => {
        if (!searchSync) {
            return;
        }
        setIsFiltering(true);
        try {
            const searchPayload = {};
            if (selectedBeneficiariesListRef.current?.length) {
                searchPayload.selected = selectedBeneficiariesListRef.current;
            }
            const result = await searchSync(searchPayload);
            processVoucherBeneficiariesWithFilter(result.beneficiaries, true);
        }
        catch (error) {
            console.error('Error fetching all beneficiaries:', error);
            if (voucherBeneficiaries?.length) {
                processVoucherBeneficiariesWithFilter(voucherBeneficiaries, true);
            }
        }
        finally {
            setIsFiltering(false);
        }
    }, [searchSync, voucherBeneficiaries, processVoucherBeneficiariesWithFilter]);
    const clearFiltersWithoutSearchSync = useCallback(() => {
        if (voucherBeneficiaries?.length) {
            processVoucherBeneficiariesWithFilter(voucherBeneficiaries, true);
        }
    }, [voucherBeneficiaries, processVoucherBeneficiariesWithFilter]);
    const clearFiltersWithFilterEnabled = useCallback(async () => {
        if (searchSync) {
            await clearFiltersWithSearchSync();
        }
        else {
            clearFiltersWithoutSearchSync();
        }
    }, [searchSync, clearFiltersWithSearchSync, clearFiltersWithoutSearchSync]);
    const filterClearer = useMemo(() => ({
        clear: async () => {
            await clearFiltersWithFilterEnabled();
        },
    }), [clearFiltersWithFilterEnabled]);
    const handleFiltersCleared = useCallback(async () => {
        if (previousFiltersRef.current === '') {
            return;
        }
        previousFiltersRef.current = '';
        setAppliedFilters(null);
        onUpdateBeneficiariesData({
            beneficiaryFilters: null,
        });
        await filterClearer.clear();
    }, [filterClearer, onUpdateBeneficiariesData]);
    const handleFilterSearch = useCallback(async () => {
        if (!searchSync) {
            return;
        }
        if (!appliedFilters) {
            await handleFiltersCleared();
            return;
        }
        const filtersChanged = previousFiltersRef.current !== searchFiltersKey;
        if (!filtersChanged) {
            return;
        }
        performSearchWithFilters();
    }, [searchSync, appliedFilters, searchFiltersKey, handleFiltersCleared, performSearchWithFilters]);
    const handleFilterSearchWhenEnabled = useCallback(async () => {
        await handleFilterSearch();
    }, [handleFilterSearch]);
    useEffect(() => {
        handleFilterSearchWhenEnabled();
    }, [handleFilterSearchWhenEnabled]);
    const handleFilterChange = useCallback((filters) => {
        const hasActiveFilters = hasAnyFilter(filters, true);
        const filtersToApply = hasActiveFilters ? filters : null;
        setAppliedFilters(filtersToApply);
        onUpdateBeneficiariesData({
            beneficiaryFilters: filtersToApply,
        });
    }, [onUpdateBeneficiariesData]);
    const handleApplyValue = useCallback(() => {
        const globalNumericValue = getUnformatedValue(globalValue);
        const isDaily = releaseType === ReleaseType.DAILY;
        let currentBusinessDays = 0;
        if (isDaily) {
            currentBusinessDays = formData.businessDays ?? businessDays;
        }
        const updatedBeneficiaries = beneficiaries.map(beneficiary => {
            if (isActiveStatus(beneficiary.statusInProduct)) {
                if (isDaily) {
                    const totalValue = globalNumericValue * currentBusinessDays;
                    return {
                        ...beneficiary,
                        productValue: totalValue,
                        productDaysQuantity: currentBusinessDays,
                        importedFields: undefined,
                    };
                }
                else {
                    return {
                        ...beneficiary,
                        productValue: globalNumericValue,
                        productDaysQuantity: 0,
                        importedFields: undefined,
                    };
                }
            }
            return beneficiary;
        });
        const type = isDaily ? 'daily' : 'monthly';
        setBeneficiaryHistory(prev => ({
            ...prev,
            [type]: {
                ...prev[type],
                values: updatedBeneficiaries,
            },
        }));
        setBeneficiaries(updatedBeneficiaries);
    }, [beneficiaries, globalValue, releaseType, businessDays, formData.businessDays]);
    const handleBusinessDaysChange = useCallback((days) => {
        setBusinessDays(days);
    }, []);
    const handleCloseAlert = useCallback(() => {
        setShowAlert(false);
    }, []);
    const handleCloseInfoAlert = useCallback(() => {
        setShowInfoAlert(false);
    }, []);
    const handleReleaseTypeChange = useCallback((value) => {
        const newReleaseType = value;
        const isDaily = newReleaseType === ReleaseType.DAILY;
        const type = isDaily ? 'daily' : 'monthly';
        const previousType = isDaily ? 'monthly' : 'daily';
        setBeneficiaryHistory(prev => ({
            ...prev,
            [previousType]: {
                ...prev[previousType],
                values: beneficiaries,
                globalValue: globalValue,
            },
        }));
        setReleaseType(newReleaseType);
        let currentBusinessDays = 0;
        if (isDaily) {
            currentBusinessDays = formData.businessDays ?? 0;
        }
        setBusinessDays(currentBusinessDays);
        const savedHistory = beneficiaryHistory[type];
        if (savedHistory.values) {
            setBeneficiaries(savedHistory.values);
        }
        else {
            const updatedBeneficiaries = beneficiaries.map(beneficiary => ({
                ...beneficiary,
                productValue: 0,
                productDaysQuantity: isDaily ? currentBusinessDays : 0,
                importedFields: undefined,
            }));
            setBeneficiaries(updatedBeneficiaries);
            setBeneficiaryHistory(prev => ({
                ...prev,
                [type]: {
                    ...prev[type],
                    values: updatedBeneficiaries,
                    globalValue: DEFAULT_CURRENCY_VALUE,
                },
            }));
        }
    }, [beneficiaries, beneficiaryHistory, globalValue, formData.businessDays]);
    const totalFromSelectedList = effectiveSelectedBeneficiariesList.reduce((total, s) => total + (s.value ?? 0), 0);
    const isFormValid = effectiveSelectedBeneficiariesList.length > 0 &&
        (releaseType !== ReleaseType.DAILY ||
            (getUnformatedValue(globalValue) > 0 &&
                effectiveSelectedBeneficiariesList.some(s => s.daysQuantity && s.daysQuantity > 0)));
    const isSimulating = simulationState === SimulationState.SIMULATING;
    const isError = simulationState === SimulationState.ERROR;
    useEffect(() => {
        if (isError) {
            setErrorMessage(t('recharge.step.beneficiaries-values.error.message'));
            setShowAlert(true);
        }
        else {
            setShowAlert(false);
        }
    }, [isError, t]);
    const handleOpenImportModal = useCallback(() => {
        setShowImportModal(true);
    }, []);
    const handleCloseImportModal = useCallback(() => {
        setShowImportModal(false);
    }, []);
    const handleManualEntry = useCallback(() => {
        setShowImportModal(false);
    }, []);
    const handleImportComplete = useCallback((validRows) => {
        const globalNumericValue = getUnformatedValue(globalValue);
        const isDaily = releaseType === ReleaseType.DAILY;
        const isMonthly = releaseType === ReleaseType.MONTHLY;
        const isDiscountColumnRendered = isBeneficiaryDiscountEnabled && (formData.payrollDeduction ?? false);
        const computeImportedProductValue = (row) => {
            if (!isDaily) {
                return row.value;
            }
            if (row.daysQuantity === 0) {
                return row.value;
            }
            return globalNumericValue * row.daysQuantity;
        };
        const buildImportedFields = () => ({
            value: isMonthly,
            days: isDaily,
            discount: isDiscountColumnRendered,
        });
        const updatedBeneficiaries = beneficiaries.map(beneficiary => {
            const normalizedDoc = normalizeCpf(beneficiary.document);
            const importedRow = validRows.find(row => row.normalizedCpf === normalizedDoc);
            if (importedRow) {
                return {
                    ...beneficiary,
                    statusInProduct: BeneficiaryStatusInProduct.ACTIVE,
                    productValue: computeImportedProductValue(importedRow),
                    productDaysQuantity: importedRow.daysQuantity,
                    productDiscountValue: importedRow.discountValue,
                    importedFields: buildImportedFields(),
                };
            }
            return {
                ...beneficiary,
                statusInProduct: BeneficiaryStatusInProduct.INACTIVE,
            };
        });
        setBeneficiaries(updatedBeneficiaries);
        const selectedList = validRows.reduce((acc, row) => {
            const beneficiary = updatedBeneficiaries.find(b => normalizeCpf(b.document) === row.normalizedCpf);
            if (beneficiary) {
                acc.push({
                    beneficiaryId: beneficiary.id,
                    value: computeImportedProductValue(row),
                    daysQuantity: row.daysQuantity,
                    discountValue: row.discountValue,
                });
            }
            return acc;
        }, []);
        selectedBeneficiariesListRef.current = selectedList;
        onUpdateBeneficiariesData({
            beneficiaries: updatedBeneficiaries,
            selectedBeneficiariesList: selectedList,
            selectedBeneficiaries: selectedList.length,
        });
        setShowImportModal(false);
    }, [
        beneficiaries,
        formData.payrollDeduction,
        globalValue,
        isBeneficiaryDiscountEnabled,
        onUpdateBeneficiariesData,
        releaseType,
    ]);
    const renderStepContent = () => {
        if (isLoadingBeneficiaries) {
            return (_jsx("div", { className: styles.loadingContainer, "data-testid": 'viewNewRechargeBeneficiariesValuesLoading', children: _jsx(Loading, {}) }));
        }
        return (_jsx(BeneficiaryValuesForm, { context: 'recharge', releaseType: releaseType, globalValue: globalValue, beneficiaries: beneficiaries, businessDays: businessDays, showAlert: showInfoAlert, isLoading: false, appliedFilters: appliedFilters, isBeneficiaryDiscountEnabled: isBeneficiaryDiscountEnabled, payrollDeduction: formData.payrollDeduction ?? false, discountType: formData.discountType, discountValue: formData.discountValue, onReleaseTypeChange: handleReleaseTypeChange, onGlobalValueChange: handleGlobalValueChange, onUpdateBeneficiaries: beneficiaryUpdater, onApplyValue: handleApplyValue, onBusinessDaysChange: handleBusinessDaysChange, onCloseAlert: handleCloseInfoAlert, onFilterChange: handleFilterChange, isFiltering: isFiltering, hasZeroValueError: showZeroValueError, filterVariant: 'legacyRecharge' }));
    };
    const renderMainCard = () => {
        const formattedTotalValue = formatCurrency(totalFromSelectedList);
        return (_jsxs(StepperCard, { currentStep: currentStep, showStepper: !headerStepper, totalValue: formattedTotalValue, headerActions: _jsxs("button", { type: 'button', className: styles.importButton, onClick: handleOpenImportModal, disabled: isLoadingBeneficiaries, "data-testid": 'buttonNewRechargeBeneficiariesValuesImportSpreadsheet', children: [_jsx(Icon, { name: 'uploadOrange', alt: '', className: styles.importButtonIcon }), t('recharge.step.beneficiaries-values.import-button')] }), actions: {
                back: {
                    label: t('recharge.button.back'),
                    onClick: handleBack,
                },
                next: {
                    labelLoading: t('recharge.step.beneficiaries-values.button.loading'),
                    label: t('recharge.button.next'),
                    onClick: handleNext,
                    loading: isSimulating,
                    disabled: !isFormValid || isSimulating || !contractData?.id || isLoadingProducts,
                },
            }, children: [headerStepper, _jsxs("div", { className: styles.stepContent, "data-testid": 'viewNewRechargeBeneficiariesValuesStepContent', children: [renderStepContent(), showAlert && errorMessage && (_jsx(Alert, { message: errorMessage, appearance: 'error', alertType: 'toast', density: 'compact', className: styles.alertMessage, duration: 8, hideCloseButton: false, onClose: handleCloseAlert }))] })] }));
    };
    const renderSummaryCard = () => (_jsx(NewRechargeSidebar, { currentStep: NewRechargeStep.BENEFICIARIES_VALUES, formData: formData, onStepChange: onStepChange }));
    return (_jsxs("div", { className: styles.pageWrapper, children: [showZeroValueError && (_jsx(Alert, { message: t('recharge.validation.zero-value-beneficiaries'), appearance: 'error', alertType: 'toast', density: 'compact', className: styles.validationAlertToast, duration: 5, hideCloseButton: false, onClose: () => setShowZeroValueError(false) })), _jsx(ContentLayout, { breadcrumbs: breadcrumbs, sidebar: renderSummaryCard(), children: renderMainCard() }), _jsx(SpreadsheetImportModal, { isOpen: showImportModal, onClose: handleCloseImportModal, onManualEntry: handleManualEntry, onImportComplete: handleImportComplete, voucherContractId: contractData?.id ?? '', voucherTypeId: formData.voucherTypeId ?? '', releaseType: releaseType, discountType: formData.discountType })] }));
}
